<script setup>
import { useRouter } from 'vue-router';
import PgeButton from '../../../pge-components/pge-button.vue';

/**
 * Where the router sends someone who reached a page their role does not allow.
 *
 * Renders inside the Hub shell on purpose: the sidebar and top bar stay, so the
 * person can see where they are and go somewhere they can actually use. The
 * previous behaviour bounced them to `/`, the public landing page, which for a
 * signed-in staff member is indistinguishable from the app breaking.
 *
 * It says what happened without dressing it up as an error — reaching a page
 * meant for another role is a normal thing to do, usually by following an old
 * link or someone else's bookmark.
 */
const router = useRouter();
</script>

<template>
  <div class="forbidden">
    <h2 class="forbidden__title">This page isn't part of your role</h2>
    <p class="forbidden__body">
      Your account doesn't have access to this part of the Hub. If you think it
      should, ask the hub owner — they control what each role can reach.
    </p>
    <div class="forbidden__actions">
      <pge-button label="Go to Policyholders" template="filled" @click="router.push('/policyholders')" />
    </div>
  </div>
</template>

<style scoped>
.forbidden {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  max-width: 520px;
  padding: var(--setup-page-padding, 40px);
}

.forbidden__title {
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: var(--XL-M-Theme-sys-light-on-surface, #1e1a1f);
  margin: 0;
}

.forbidden__body {
  font-size: 14px;
  line-height: 20px;
  color: var(--XL-M-Theme-sys-light-on-surface-variant, #4b444d);
  margin: 0;
}

.forbidden__actions {
  margin-top: 8px;
}

/* pge-button's container is height:100% and balloons in a column flex parent. */
.forbidden__actions :deep(.pge-button-container) {
  height: auto;
}
</style>
